import { useQuery, useQueryClient } from '@tanstack/react-query';

import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { useSessionContext } from '../../../../js/app/contexts/SessionContext';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { Button } from '../../components/button';
import { DrawerCloseButton } from '../../components/drawer';
import { Heading } from '../../components/heading';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../components/tabs';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { AddSpecialtyDialog } from './AddSpecialtyDialog';
import { EditFacilityDialog } from './EditFacilityDialog';
import {
  enrichDynamicFieldsWithChoices,
  FACILITY_DISPLAY_CODE_CATEGORIES,
} from './enrichFacilityDynamicFields';
import {
  FACILITY_QUERY_KEY,
  FACILITY_SPECIALTIES_QUERY_KEY,
  FACILITY_TYPE_FIELDS_QUERY_KEY,
} from './Facilities/queryKeys';
import { FacilityDetailsPanel } from './FacilityDetailsPanel';
import { FacilitySettingsPanel } from './FacilitySettingsPanel';
import { FacilitySpecialtiesList } from './FacilitySpecialtiesList';

type FacilityDrawerProps = {
  facilityId: string;
  onClose?: () => void;
  showOrganization?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FacilityDrawer: React.FC<FacilityDrawerProps> = ({
  facilityId,
  onClose,
  showOrganization = false,
}) => {
  const { user, audienceIsStaff } = useSessionContext();
  const canEditFacility = Boolean(
    audienceIsStaff || user?.group_names?.includes('manage_facilities')
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddSpecialtyDialogOpen, setIsAddSpecialtyDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    ...FACILITY_DISPLAY_CODE_CATEGORIES,
  ]);

  const { data: facilityData, isLoading: isFacilityLoading } = useQuery({
    queryKey: [FACILITY_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({ id: facilityId }),
    enabled: !!facilityId,
  });

  const { data: specialtiesData } = useQuery({
    queryKey: [FACILITY_SPECIALTIES_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: facilityId,
      }),
    enabled: !!facilityId,
  });

  const { data: facilityTypeFields } = useQuery({
    queryKey: [FACILITY_TYPE_FIELDS_QUERY_KEY],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesFacilityTypeFieldsList({}),
    enabled: !!facilityData,
  });

  const codeDefMaps = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const maps: any = {};
    Object.entries(codeDefinitionsByCategory).forEach(([category, defs]) => {
      maps[category] = {};
      defs.forEach((def) => {
        maps[category][def.code] = def.title;
      });
    });
    return maps;
  }, [codeDefinitionsByCategory]);

  const facilityTypesWithDynamicFields = useMemo(() => {
    if (!facilityTypeFields?.results) return {};
    return facilityTypeFields.results.reduce((acc: Record<string, any[]>, item) => {
      const parsed =
        typeof item.dynamic_fields === 'string'
          ? (JSON.parse(item.dynamic_fields || '[]') as any[])
          : (item.dynamic_fields as any[]);
      acc[item.facility_type] = parsed;
      return acc;
    }, {});
  }, [facilityTypeFields]);

  const facility = useMemo(() => {
    if (!facilityData || !codeDefinitionsByCategory) return null;
    const rawFacilityTypeFields = facilityData.facility_type_fields || [];
    const facilityTypeFieldsArray =
      typeof rawFacilityTypeFields === 'string'
        ? (JSON.parse(rawFacilityTypeFields || '[]') as any[])
        : rawFacilityTypeFields;
    return {
      ...facilityData,
      facility_type_fields: enrichDynamicFieldsWithChoices(
        facilityTypeFieldsArray,
        codeDefinitionsByCategory
      ),
    };
  }, [facilityData, codeDefinitionsByCategory]);

  const specialtyOptions = useMemo(() => {
    if (!codeDefinitionsByCategory) return [];
    const specialtyDefs = codeDefinitionsByCategory.Specialty || [];
    return specialtyDefs.map((opt) => ({
      value: opt.code,
      label: opt.title,
    }));
  }, [codeDefinitionsByCategory]);

  const attachedSpecialties = useMemo(() => {
    if (!specialtiesData?.results || !codeDefinitionsByCategory) return [];
    return specialtiesData.results.map((specialty) => ({
      ...specialty,
      dynamic_fields: enrichDynamicFieldsWithChoices(
        typeof specialty.dynamic_fields === 'string'
          ? JSON.parse(specialty.dynamic_fields)
          : specialty.dynamic_fields,
        codeDefinitionsByCategory
      ),
    }));
  }, [specialtiesData, codeDefinitionsByCategory]);

  const attachedSpecialtyCodes = new Set(attachedSpecialties.map((s) => String(s.specialty)));
  const unattachedSpecialtyOptions = specialtyOptions.filter(
    (opt) => !attachedSpecialtyCodes.has(opt.value)
  );

  const handleAddSpecialties = async (selectedSpecialtyCodes: string[]) => {
    if (!facilityId) return;
    try {
      await HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesCreate({
        requestBody: { facility: facilityId, specialties: selectedSpecialtyCodes as any } as any,
        facility: facilityId,
      });
      toast.success('Specialties added!');
      // Invalidate query to refetch specialties
      queryClient.invalidateQueries([FACILITY_SPECIALTIES_QUERY_KEY, facilityId]);
      setIsAddSpecialtyDialogOpen(false);
    } catch (err) {
      handleApiError(err, { messagePrefix: 'Failed to add specialties' });
    }
  };

  if (isFacilityLoading || !facility) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading facility details...</div>
      </div>
    );
  }

  const organizationName = facility.healthcare_entity?.name;

  return (
    <div className="tw:flex tw:flex-col tw:mt-6">
      {/* Org/Facility Name Row */}
      <div className="tw:flex tw:flex-col tw:gap-1 tw:mb-4">
        <div className="tw:flex tw:justify-between tw:items-center">
          <Heading level={1} className="tw:!text-brand-green-0 tw:!mb-0">
            {facility.name}
          </Heading>
          {onClose && <DrawerCloseButton onClick={onClose} />}
        </div>
        {showOrganization && organizationName && (
          <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
            <span className="tw:truncate tw:italic">{organizationName}</span>
          </div>
        )}
        <div className="tw:flex tw:items-center tw:text-sm tw:gap-4 tw:text-zinc-400">
          <span className="tw:flex tw:items-center tw:gap-2">
            <i className="fa-light fa-hospital" />
            <span>
              {codeDefMaps['FacilityTypes']?.[facility.facility_type] || facility.facility_type}
            </span>
          </span>
          <span className="tw:flex tw:items-center tw:gap-2">
            <i className="fa-light fa-location-dot" />
            <span>
              {facility.city ? `${facility.city}, ` : ''}{' '}
              {facility.state ? `${facility.state}` : ''}
              {!facility.city && !facility.state ? '--' : ''}
            </span>
          </span>
        </div>
      </div>
      {/* Tabs */}
      <Tabs defaultTab="details">
        <TabsList className="tw:border-t tw:border-b tw:border-zinc-200">
          <TabsButton
            tab="details"
            icon={
              <i className="fa-solid fa-memo-circle-info tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            }
          >
            Facility Details
          </TabsButton>
          <TabsButton
            tab="specialties"
            icon={
              <i className="fa-regular fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            }
          >
            Specialties
          </TabsButton>
          <TabsButton
            tab="settings"
            icon={
              <i className="fa-regular fa-gear tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            }
          >
            Settings
          </TabsButton>
        </TabsList>

        <TabsContent tab="details">
          <FacilityDetailsPanel
            facility={facility}
            codeDefMaps={codeDefMaps}
            canEdit={canEditFacility}
            onEditClick={() => setIsEditDialogOpen(true)}
            facilityId={facilityId}
          />
        </TabsContent>

        <TabsContent tab="specialties">
          {/* Specialties */}
          <section className="tw:pb-8">
            <div className="tw:flex tw:justify-end tw:mb-4">
              {canEditFacility && (
                <Button
                  color="brand-green-outline"
                  onClick={() => setIsAddSpecialtyDialogOpen(true)}
                >
                  <i className="fa-solid fa-plus" />
                  Add Specialty
                </Button>
              )}
            </div>
            <FacilitySpecialtiesList
              facilityId={facilityId}
              specialtyOptions={specialtyOptions}
              specialties={attachedSpecialties}
              onUpdate={() =>
                queryClient.invalidateQueries([FACILITY_SPECIALTIES_QUERY_KEY, facilityId])
              }
              canEdit={canEditFacility}
            />
          </section>
        </TabsContent>

        <TabsContent tab="settings">
          <FacilitySettingsPanel
            facility={facility}
            canEdit={canEditFacility}
            onEditClick={() => setIsEditDialogOpen(true)}
          />
        </TabsContent>
      </Tabs>

      <EditFacilityDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        facilityId={facilityId}
        initialData={facility}
        onSuccess={() => {
          setIsEditDialogOpen(false);
          queryClient.invalidateQueries([FACILITY_QUERY_KEY, facilityId]);
        }}
        facilityTypesWithDynamicFields={facilityTypesWithDynamicFields}
      />

      <AddSpecialtyDialog
        open={isAddSpecialtyDialogOpen}
        onClose={() => setIsAddSpecialtyDialogOpen(false)}
        specialtyOptions={unattachedSpecialtyOptions}
        onSave={handleAddSpecialties}
      />
    </div>
  );
};
